import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, BookOpen, Search, X, Eye } from "lucide-react";
import { BlogSubscribeBox } from "../../BlogSubscribeBox";

const API = process.env.REACT_APP_BACKEND_URL || "";
export const BLOG_LAST_READ_KEY = "eviq_blog_last_read";

const MD = {
  h2: (p) => <h2 className="text-base font-semibold text-white mt-6 mb-2" {...p} />,
  h3: (p) => <h3 className="text-sm font-semibold text-white mt-5 mb-1.5" {...p} />,
  p: (p) => <p className="text-[13px] text-zinc-300 leading-relaxed mb-3" {...p} />,
  ul: (p) => <ul className="list-disc pl-4 mb-3 space-y-1 text-[13px] text-zinc-300" {...p} />,
  ol: (p) => <ol className="list-decimal pl-4 mb-3 space-y-1 text-[13px] text-zinc-300" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
  a: (p) => <a className="text-[#39FF14] underline-offset-2" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-[#39FF14]/50 pl-3 text-zinc-400 italic mb-3" {...p} />,
  table: (p) => (
    <div className="overflow-x-auto mb-3 rounded-lg border border-white/10">
      <table className="w-full text-xs" {...p} />
    </div>
  ),
  thead: (p) => <thead className="border-b border-white/10" {...p} />,
  th: (p) => <th className="px-2 py-1.5 text-left font-mono text-[9px] uppercase tracking-wider text-zinc-500 font-medium" {...p} />,
  td: (p) => <td className="px-2 py-1.5 text-[12px] text-zinc-300 border-b border-white/5" {...p} />,
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

/** BlogView — in-app blog tab: article list + inline reader. */
export const BlogView = () => {
  const [rows, setRows] = useState(null);
  const [post, setPost] = useState(null); // opened article (full doc)
  const [q, setQ] = useState("");
  const [tag, setTag] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/blog`)
      .then((r) => (r.ok ? r.json() : { rows: [] }))
      .then((d) => {
        const list = d.rows || [];
        setRows(list);
        // Mark the newest article as read — clears the tab badge next launch.
        if (list[0]?.published_at) {
          try { window.localStorage.setItem(BLOG_LAST_READ_KEY, list[0].published_at); } catch { /* noop */ }
        }
      })
      .catch(() => setRows([]));
  }, []);

  const open = (slug) => {
    setPost("loading");
    fetch(`${API}/api/blog/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setPost)
      .catch(() => setPost(null));
    window.scrollTo(0, 0);
  };

  if (post && post !== "loading" && post !== null && typeof post === "object") {
    return (
      <div className="pt-4" data-testid="app-blog-article">
        <button
          onClick={() => setPost(null)}
          data-testid="app-blog-back"
          className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All articles
        </button>
        <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[9px] uppercase tracking-wider text-zinc-500">
          <span>{fmt(post.published_at)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {post.reading_min} min</span>
        </div>
        <h1 className="mt-2 text-xl font-semibold text-white leading-snug">{post.title}</h1>
        <div className="mt-4">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{post.body_md}</ReactMarkdown>
        </div>
      </div>
    );
  }

  const n = (s) => (s || "").toLowerCase();
  const query = n(q).trim();
  const filtered = (rows || []).filter(
    (p) =>
      (!tag || (p.tags || []).includes(tag)) &&
      (!query ||
        n(p.title).includes(query) ||
        n(p.excerpt).includes(query) ||
        (p.tags || []).some((t) => n(t).includes(query)))
  );
  const allTags = [...new Set((rows || []).flatMap((p) => p.tags || []))].slice(0, 8);
  const searching = query !== "" || tag !== null;

  return (
    <div className="pt-4" data-testid="app-blog-view">
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">The EVIQ blog</div>
      <h2 className="mt-1 text-lg font-semibold text-white">Daily guides for every EV owner.</h2>

      {rows !== null && rows.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="app-blog-search-bar">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles…"
              data-testid="app-blog-search-input"
              className="w-full rounded-lg border border-white/10 bg-white/[0.03] pl-8 pr-8 py-2 text-[12px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#39FF14]/50 transition-colors"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {allTags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTag(tag === t ? null : t)}
                  data-testid={`app-blog-tag-${t}`}
                  className="rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider transition-colors"
                  style={
                    tag === t
                      ? { borderColor: "rgba(57,255,20,0.6)", color: "#39FF14", background: "rgba(57,255,20,0.1)" }
                      : { borderColor: "rgba(255,255,255,0.12)", color: "#a1a1aa" }
                  }
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {post === "loading" && <div className="mt-4 font-mono text-[11px] text-zinc-500">Opening article…</div>}

      <div className="mt-4 space-y-3">
        {rows === null ? (
          <div className="font-mono text-[11px] text-zinc-500">Loading articles…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 p-6 text-center" data-testid="app-blog-empty">
            <BookOpen className="w-5 h-5 mx-auto text-zinc-600" />
            <div className="mt-2 text-[12px] text-zinc-400">First articles arriving shortly — check back tomorrow.</div>
          </div>
        ) : searching && filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 p-6 text-center" data-testid="app-blog-no-results">
            <div className="text-[12px] text-zinc-400">No articles match — try another keyword.</div>
          </div>
        ) : (
          filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => open(p.slug)}
              data-testid="app-blog-card"
              className="w-full text-left rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-[#39FF14]/40 transition-colors"
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                <span>{fmt(p.published_at)}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {p.reading_min} min</span>
                {(p.tags || []).slice(0, 2).map((t) => (
                  <span key={t} className="rounded-full border border-white/10 px-1.5 py-0.5 text-[#39FF14]">{t}</span>
                ))}
              </div>
              <div className="mt-1.5 text-[14px] font-semibold text-white leading-snug">{p.title}</div>
              {p.excerpt && <p className="mt-1 text-[11px] text-zinc-500 leading-relaxed line-clamp-2">{p.excerpt}</p>}
              <div
                className="mt-2 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-zinc-600"
                data-testid="app-blog-card-reads"
              >
                <Eye className="w-2.5 h-2.5" /> {(p.views || 0).toLocaleString()} read{(p.views || 0) === 1 ? "" : "s"}
              </div>
            </button>
          ))
        )}
        {rows !== null && rows.length > 0 && (
          <div className="pt-2">
            <BlogSubscribeBox compact />
          </div>
        )}
      </div>
    </div>
  );
};
